import { Controller } from "@hotwired/stimulus";

// Drives the admin "Record Payment" 4-step modal (Payer → Products → Notify → Review).
// Step 2 is a stack of removable rows; each row picks a Kind, reveals kind-specific fields,
// Qty, editable USD, and (priced kinds) clickable tier pills + a text price hint. All state
// lives on the client until Confirm posts one JSON payload; the modal fully resets on close.
export default class extends Controller {
  static targets = [
    "stepper", "stepPill", "step",
    "error", "errorMessage",
    "rowsContainer", "itemRow", "rowFields", "totalAmount",
    "notifyDetail", "channelInApp", "channelEmail", "inAppUnavailableHint", "notifyEmailContainer", "notifyEmail",
    "previewPayer", "previewItems", "previewTotal", "previewNotify",
    "backBtn", "nextBtn", "confirmBtn",
    "rowTemplate", "productFieldsTemplate", "courseFieldsTemplate", "fundingFieldsTemplate",
    "customFieldsTemplate", "tierOptionTemplate"
  ];

  static values = {
    url: String,
    fundingProgramsUrl: String,
    requirementsUrl: String
  };

  connect() {
    this.currentStep = 1;
    this._payerEmail = "";
    this._programs = [];
    this._requirementsByProgram = {};

    this._boundReset = this._reset.bind(this);
    this._boundSelect = this._onSelectChange.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);
    this.element.addEventListener("app-select:change", this._boundSelect);

    this.addRow();
    this._showStep();
    this._syncNotifyAvailability();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
    this.element.removeEventListener("app-select:change", this._boundSelect);
  }

  // ── Delegated select handling ────────────────────────────────────────

  _onSelectChange(event) {
    const role = event.target?.dataset?.role;
    if (!role) return;
    if (role === "payer") return this.onPayerChange(event);

    const row = event.target.closest(".record-payment-row");
    if (!row) return;

    switch (role) {
      case "product": return this.onItemPicked(row, "productId", event.target.value, this._parsePayload(event));
      case "course": return this.onItemPicked(row, "courseId", event.target.value, this._parsePayload(event));
      case "program": return this.onProgramChange(row, event.target.value);
      case "requirement": return this.onRequirementChange(row, event.target.value);
      case "item": return this.onFundingItemChange(row, event.target.value);
    }
  }

  // ── Step navigation ──────────────────────────────────────────────────

  nextStep() {
    const error = this._validateStep(this.currentStep);
    if (error) {
      this._showError(error);
      return;
    }
    this._hideError();
    if (this.currentStep < 4) {
      this.currentStep += 1;
      this._showStep();
    }
  }

  prevStep() {
    this._hideError();
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      this._showStep();
    }
  }

  _showStep() {
    this.stepTargets.forEach((el) => {
      el.classList.toggle("d-none", Number(el.dataset.step) !== this.currentStep);
    });
    this.stepPillTargets.forEach((pill) => {
      const step = Number(pill.dataset.step);
      pill.classList.toggle("is-active", step === this.currentStep);
      pill.classList.toggle("is-complete", step < this.currentStep);
      if (step === this.currentStep) {
        pill.setAttribute("aria-current", "step");
      } else {
        pill.removeAttribute("aria-current");
      }
    });

    this.backBtnTarget.classList.toggle("d-none", this.currentStep === 1);
    this.nextBtnTarget.classList.toggle("d-none", this.currentStep === 4);
    this.confirmBtnTarget.classList.toggle("d-none", this.currentStep !== 4);

    if (this.currentStep === 3) this._syncNotifyAvailability();
    if (this.currentStep === 4) this._renderPreview();

    this._focusFirstField();
  }

  _focusFirstField() {
    const step = this.stepTargets.find((el) => Number(el.dataset.step) === this.currentStep);
    const field = step?.querySelector("input:not([type=hidden]), button.app-select-button");
    if (field) requestAnimationFrame(() => field.focus());
  }

  _validateStep(step) {
    if (step === 2) {
      const started = this.itemRowTargets.filter((row) => row.dataset.kind);
      const incomplete = started.filter((row) => !this._rowComplete(row));
      this.itemRowTargets.forEach((row) => row.classList.remove("is-invalid"));
      if (incomplete.length) {
        incomplete.forEach((row) => row.classList.add("is-invalid"));
        return "Finish or remove the highlighted item.";
      }
      return this._collectItems().length ? null : "Add at least one item.";
    }
    if (step === 3) {
      if (!this._notifyEnabled()) return null;
      const channels = this._notifyChannels();
      if (!channels.length) return "Choose at least one way to notify the payer.";
      if (channels.includes("email") && !this._isValidEmail(this._notifyEmailValue())) {
        return "Enter a valid email address to notify by email.";
      }
    }
    return null; // step 1 (payer optional) and step 4 have no gate
  }

  // ── Step 1: payer ────────────────────────────────────────────────────

  async onPayerChange(event) {
    const previousPayerEmail = this._payerEmail;
    this._payerEmail = event?.detail?.email || "";
    this._syncNotifyEmailToPayer(previousPayerEmail);
    this._syncNotifyAvailability();
    this._hideError();
    await this._fetchPrograms();
    this._refreshFundingRows();
  }

  // Keep the "Send to" address tracking the payer's email (fill on select, clear on removal)
  // — but never overwrite an address the admin typed by hand.
  _syncNotifyEmailToPayer(previousPayerEmail) {
    const current = this._notifyEmailValue();
    if (!current || current === previousPayerEmail) {
      this.notifyEmailTarget.value = this._payerEmail;
    }
  }

  async _fetchPrograms() {
    const userId = this._payerValue();
    if (!userId) {
      this._programs = [];
      return;
    }
    const programs = await this._fetchJson(this.fundingProgramsUrlValue, { user_id: userId });
    this._programs = Array.isArray(programs) ? programs : [];
  }

  // Re-seed every existing funding row's program select and clear its downstream selection.
  _refreshFundingRows() {
    this.itemRowTargets
      .filter((row) => row.dataset.kind === "funding")
      .forEach((row) => this._populatePrograms(row));
  }

  // ── Step 2: rows ─────────────────────────────────────────────────────

  addRow() {
    this.rowsContainerTarget.appendChild(this.rowTemplateTarget.content.cloneNode(true));
    const row = this.rowsContainerTarget.lastElementChild;
    this._scopeKindRadios(row);
    this.recalcTotal();
    row.querySelector(".record-payment-kind-card .radio-input")?.focus();
  }

  // Every row is cloned from the same template, so its Kind radios arrive sharing one name and
  // id — leaving them would make each row's choice uncheck the others'.
  _scopeKindRadios(row) {
    this._rowSeq = (this._rowSeq || 0) + 1;
    row.querySelectorAll(".record-payment-kind-card .radio-input").forEach((input) => {
      input.name = `${input.name}_${this._rowSeq}`;
      input.id = `${input.id}_${this._rowSeq}`;
    });
  }

  removeRow(event) {
    event.currentTarget.closest(".record-payment-row")?.remove();
    if (this.itemRowTargets.length === 0) this.addRow();
    this.recalcTotal();
  }

  onKindSelected(event) {
    this.onKindChange(event.target.closest(".record-payment-row"), event.target.value);
  }

  onKindChange(row, kind) {
    row.dataset.kind = kind || "";
    ["itemName", "pricingTier", "productId", "courseId", "fundingProgramId", "requirementId"].forEach(
      (key) => delete row.dataset[key]
    );
    row.classList.remove("is-invalid");

    const fields = row.querySelector('[data-record-payment-modal-target="rowFields"]');
    fields.innerHTML = "";
    const template = this._fieldsTemplateFor(kind);
    if (template) fields.appendChild(template.content.cloneNode(true));

    if (kind === "funding") this._populatePrograms(row);

    this.recalcTotal();
    fields.querySelector("input:not([type=hidden]), button.app-select-button")?.focus();
  }

  _fieldsTemplateFor(kind) {
    switch (kind) {
      case "payment_product": return this.productFieldsTemplateTarget;
      case "course": return this.courseFieldsTemplateTarget;
      case "funding": return this.fundingFieldsTemplateTarget;
      case "custom": return this.customFieldsTemplateTarget;
      default: return null;
    }
  }

  // product / course
  onItemPicked(row, idKey, id, payload) {
    row.dataset[idKey] = id;
    row.dataset.itemName = payload.name || "";
    this._renderTiers(row, payload.tiers || []);
    this.recalcTotal();
  }

  // funding: program → requirements
  async onProgramChange(row, programId) {
    row.dataset.fundingProgramId = programId || "";
    delete row.dataset.requirementId;
    delete row.dataset.itemName;
    this._setRowOptions(row, "requirement", [], "Select a program first");
    this._setRowOptions(row, "item", [], "Select a main item first");
    this._renderTiers(row, []);
    if (!programId) return;

    const requirements = await this._fetchRequirements(programId);
    if (row.dataset.fundingProgramId !== programId) return; // a newer program selection superseded this
    this._setRowOptions(
      row, "requirement",
      requirements.map((r) => [r.name, String(r.id)]),
      requirements.length ? "Select a main item" : "No main items for this program"
    );
  }

  // funding: requirement → items
  onRequirementChange(row, requirementId) {
    row.dataset.requirementId = requirementId || "";
    delete row.dataset.itemName;
    this._renderTiers(row, []);
    const requirement = this._requirementFor(row);
    const items = requirement ? requirement.items : [];
    this._setRowOptions(
      row, "item",
      items.map((item) => [item.name, item.name]),
      items.length ? "Select an item" : "No items"
    );
  }

  // funding: item → name + price
  onFundingItemChange(row, itemName) {
    const requirement = this._requirementFor(row);
    const item = requirement?.items.find((entry) => entry.name === itemName);
    if (!item) return;

    row.dataset.itemName = item.name;
    const tiers = [{ label: "Regular", tier: "regular", amount: Number(item.regular_price) || 0 }];
    if (Number(item.early_price) > 0) {
      tiers.push({ label: "Early bird", tier: "early", amount: Number(item.early_price) });
    }
    tiers.sort((a, b) => b.amount - a.amount);
    this._renderTiers(row, tiers);
    this.recalcTotal();
  }

  _requirementFor(row) {
    const requirements = this._requirementsByProgram[row.dataset.fundingProgramId] || [];
    return requirements.find((r) => String(r.id) === row.dataset.requirementId);
  }

  async _fetchRequirements(programId) {
    if (this._requirementsByProgram[programId]) return this._requirementsByProgram[programId];
    const requirements = await this._fetchJson(this.requirementsUrlValue, {
      funding_program_id: programId,
      user_id: this._payerValue()
    });
    this._requirementsByProgram[programId] = Array.isArray(requirements) ? requirements : [];
    return this._requirementsByProgram[programId];
  }

  _populatePrograms(row) {
    this._setRowOptions(
      row, "program",
      this._programs.map((program) => [program.name, String(program.id)]),
      this._programs.length ? "Select a program" : "Select a user in step 1 first"
    );
    this._setRowOptions(row, "requirement", [], "Select a program first");
    this._setRowOptions(row, "item", [], "Select a main item first");
    delete row.dataset.fundingProgramId;
    delete row.dataset.requirementId;
    delete row.dataset.itemName;
    this._renderTiers(row, []);
  }

  // ── Pricing (pills + hint) ───────────────────────────────────────────

  _renderTiers(row, tiers) {
    const wrap = row.querySelector('[data-role="tiers"]');
    if (wrap) wrap.innerHTML = "";
    delete row.dataset.pricingTier;
    if (!tiers.length) return;

    tiers.forEach((tier) => {
      const pill = this.tierOptionTemplateTarget.content.firstElementChild.cloneNode(true);
      pill.querySelector(".record-payment-tier-pill-label").textContent = tier.label;
      pill.querySelector(".record-payment-tier-pill-amount").textContent = this._money(tier.amount);
      pill.dataset.tier = tier.tier;
      pill.dataset.amount = tier.amount;
      wrap?.appendChild(pill);
    });
    if (wrap?.firstElementChild) this._selectTier(row, wrap.firstElementChild);
  }

  chooseTier(event) {
    this._selectTier(event.currentTarget.closest(".record-payment-row"), event.currentTarget);
  }

  _selectTier(row, pill) {
    row.querySelectorAll(".record-payment-tier-pill").forEach((el) => {
      el.classList.remove("is-selected");
      el.setAttribute("aria-pressed", "false");
    });
    pill.classList.add("is-selected");
    pill.setAttribute("aria-pressed", "true");
    const priceInput = row.querySelector('[data-role="price"]');
    if (priceInput) priceInput.value = Number(pill.dataset.amount).toFixed(2);
    row.dataset.pricingTier = pill.dataset.tier;
    this.recalcTotal();
  }

  recalcTotal() {
    let total = 0;
    this.itemRowTargets.forEach((row) => {
      if (!this._rowComplete(row)) return;
      total += this._rowQty(row) * this._rowPrice(row);
    });
    this.totalAmountTarget.textContent = total.toFixed(2);
  }

  // ── Step 3: notify ───────────────────────────────────────────────────

  onNotifyEnabledChange() {
    const enabled = this._notifyEnabled();
    this._toggle(this.notifyDetailTarget, enabled);
    if (enabled) {
      // Default to reaching the payer every way we can: email always, in-app when there's a user.
      this.channelEmailTarget.checked = true;
      this.channelInAppTarget.checked = this._hasPayer();
      if (!this._notifyEmailValue()) this.notifyEmailTarget.value = this._defaultNotifyEmail();
    }
    this._syncNotifyAvailability();
    this._hideError();
  }

  onNotifyChannelChange() {
    this._syncNotifyAvailability();
    if (this.channelEmailTarget.checked && !this._notifyEmailValue()) {
      this.notifyEmailTarget.value = this._defaultNotifyEmail();
    }
    this._hideError();
  }

  _syncNotifyAvailability() {
    const hasUser = this._hasPayer();
    this.channelInAppTarget.disabled = !hasUser;
    if (!hasUser) this.channelInAppTarget.checked = false;
    this._toggle(this.inAppUnavailableHintTarget, !hasUser);
    this._toggle(this.notifyEmailContainerTarget, this._notifyEnabled() && this.channelEmailTarget.checked);
  }

  _defaultNotifyEmail() {
    return this._payerEmail || "";
  }

  // ── Step 4: preview ──────────────────────────────────────────────────

  _renderPreview() {
    this.previewPayerTarget.textContent = this._payerSummary();

    this.previewItemsTarget.innerHTML = "";
    this._collectItems().forEach((item) => {
      const row = document.createElement("tr");
      row.innerHTML = `
        <td class="col-name"></td>
        <td class="col-qty"></td>
        <td class="col-price"></td>
        <td class="col-total"></td>`;
      row.querySelector(".col-name").textContent = item.name;
      row.querySelector(".col-qty").textContent = item.quantity;
      row.querySelector(".col-price").textContent = this._money(item.unit_price);
      row.querySelector(".col-total").textContent = this._money(item.quantity * item.unit_price);
      this.previewItemsTarget.appendChild(row);
    });

    this.previewTotalTarget.textContent = this.totalAmountTarget.textContent;
    this.previewNotifyTarget.textContent = this._notifySummary();
  }

  _payerSummary() {
    if (this._hasPayer()) {
      const typeahead = this._payerInput?.closest("[data-controller~='async-typeahead']");
      const input = typeahead?.querySelector('[data-async-typeahead-target="input"]');
      const label = input?.value?.trim();
      return label || "Selected user";
    }
    return "No user (unassigned)";
  }

  _notifySummary() {
    if (!this._notifyEnabled()) return "No notification";
    const channels = this._notifyChannels();
    const parts = [];
    if (channels.includes("in_app")) parts.push(`In-app to ${this._payerSummary()}`);
    if (channels.includes("email")) parts.push(`Email to ${this._notifyEmailValue()}`);
    return parts.length ? parts.join(" · ") : "No notification";
  }

  // ── Submit ───────────────────────────────────────────────────────────

  async submit() {
    const payload = {
      record_payment: {
        payer_id: this._payerValue() || null,
        notify: {
          enabled: this._notifyEnabled() ? "1" : "0",
          channels: this._notifyChannels(),
          email: this._notifyEmailValue() || null
        },
        items: this._collectItems()
      }
    };

    this.confirmBtnTarget.disabled = true;
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data.success) {
        window.location.assign(data.redirect_url);
      } else {
        this._showError((data.errors || ["Could not record the payment."]).join(", "));
        this.confirmBtnTarget.disabled = false;
      }
    } catch (e) {
      this._showError("Something went wrong. Please try again.");
      this.confirmBtnTarget.disabled = false;
    }
  }

  _collectItems() {
    return this.itemRowTargets
      .filter((row) => row.dataset.kind && this._rowComplete(row))
      .map((row) => this._rowToItem(row));
  }

  _rowToItem(row) {
    const kind = row.dataset.kind;
    const item = {
      kind,
      name: this._rowName(row),
      quantity: this._rowQty(row),
      unit_price: this._rowPrice(row),
      pricing_tier: row.dataset.pricingTier || null
    };
    if (kind === "payment_product") item.payment_product_id = row.dataset.productId;
    if (kind === "course") item.course_id = row.dataset.courseId;
    if (kind === "funding") item.funding_program_id = row.dataset.fundingProgramId;
    return item;
  }

  _rowComplete(row) {
    const kind = row.dataset.kind;
    if (!kind) return false;
    if (!this._rowName(row) || this._rowQty(row) <= 0) return false;
    if (kind === "payment_product") return !!row.dataset.productId;
    if (kind === "course") return !!row.dataset.courseId;
    if (kind === "funding") return !!row.dataset.fundingProgramId && !!row.dataset.itemName;
    return true; // custom
  }

  _rowName(row) {
    if (row.dataset.kind === "custom") {
      return (row.querySelector('[data-role="name"]')?.value || "").trim();
    }
    return (row.dataset.itemName || "").trim();
  }

  _rowQty(row) {
    return parseInt(row.querySelector('[data-role="qty"]')?.value, 10) || 0;
  }

  _rowPrice(row) {
    return parseFloat(row.querySelector('[data-role="price"]')?.value) || 0;
  }

  // ── Reset ────────────────────────────────────────────────────────────

  _reset() {
    this.currentStep = 1;
    this._payerEmail = "";
    this._programs = [];
    this._requirementsByProgram = {};

    this._clearAsyncTypeahead(this._payerInput);

    this.rowsContainerTarget.innerHTML = "";
    this.addRow();

    this._setRadio("record_payment[notify_enabled]", "no");
    this.channelInAppTarget.checked = false;
    this.channelEmailTarget.checked = false;
    this._toggle(this.notifyDetailTarget, false);
    this._toggle(this.notifyEmailContainerTarget, false);
    this.notifyEmailTarget.value = "";

    this.confirmBtnTarget.disabled = false;
    this._hideError();
    this.recalcTotal();
    this._showStep();
    this._syncNotifyAvailability();
  }

  // ── Helpers ──────────────────────────────────────────────────────────

  get _payerInput() { return this.element.querySelector('input[name="record_payment[payer_id]"]'); }
  _payerValue() { return this._payerInput?.value || ""; }
  _hasPayer() { return !!this._payerValue(); }

  _notifyEmailValue() { return this.notifyEmailTarget.value.trim(); }

  _notifyEnabled() {
    return this.element.querySelector('input[name="record_payment[notify_enabled]"]:checked')?.value === "yes";
  }

  // The channels the admin has selected — in-app is only real when a user is attached.
  _notifyChannels() {
    const channels = [];
    if (this.channelInAppTarget.checked && !this.channelInAppTarget.disabled) channels.push("in_app");
    if (this.channelEmailTarget.checked) channels.push("email");
    return channels;
  }

  _setRadio(name, value) {
    const input = this.element.querySelector(`input[name="${name}"][value="${value}"]`);
    if (input) input.checked = true;
  }

  // Set the options of a row's app-select identified by its role (product/program/...).
  _setRowOptions(row, role, options, prompt) {
    this._setOptions(row.querySelector(`[data-role="${role}"]`), options, prompt);
  }

  _parsePayload(event) {
    try {
      return JSON.parse(event?.detail?.payload || "{}");
    } catch (e) {
      return {};
    }
  }

  _isValidEmail(email) {
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  _money(value) {
    return `$${(Number(value) || 0).toFixed(2)}`;
  }

  _toggle(el, show) {
    if (el) el.classList.toggle("d-none", !show);
  }

  _showError(message) {
    this.errorMessageTarget.textContent = message;
    this.errorTarget.classList.remove("d-none");
  }

  _hideError() {
    this.errorTarget.classList.add("d-none");
  }

  _fetchJson(url, params) {
    const qs = new URLSearchParams(params).toString();
    return fetch(`${url}?${qs}`, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : []))
      .catch(() => []);
  }

  _setOptions(hiddenInput, options, prompt) {
    if (!hiddenInput) return;
    const wrapper = hiddenInput.parentElement;
    const panel = wrapper.querySelector(".app-select-panel");
    const label = wrapper.querySelector('[data-app-select-target="label"]');

    panel.querySelectorAll(".app-select-option, .app-select-group").forEach((el) => el.remove());

    options.forEach(([optLabel, optValue]) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "app-select-option";
      btn.dataset.value = optValue;
      btn.dataset.label = optLabel;
      btn.setAttribute("data-app-select-target", "option");
      btn.setAttribute("data-action", "click->app-select#selectOption keydown->app-select#listKeydown");
      const span = document.createElement("span");
      span.textContent = optLabel;
      btn.appendChild(span);
      panel.appendChild(btn);
    });

    hiddenInput.value = "";
    if (label) label.textContent = prompt;
  }

  _clearAsyncTypeahead(hiddenInput) {
    if (!hiddenInput) return;
    const root = hiddenInput.closest("[data-controller~='async-typeahead']");
    const controller =
      root && this.application.getControllerForElementAndIdentifier(root, "async-typeahead");
    if (controller) {
      controller.clear({ dispatch: false });
      return;
    }
    hiddenInput.value = "";
  }
}
